import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { Logo } from "../../components/Logo";
import { Loader2, Mail, Lock, AlertCircle } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) navigate("/admin/dashboard");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 relative overflow-hidden flex-col justify-between p-12">
        <Logo light size="lg" />
        <div className="relative z-10">
          <h1 className="font-head text-4xl font-extrabold text-white leading-tight">Centralized registration &amp; payment management.</h1>
          <p className="text-slate-400 mt-4 text-lg max-w-md">Create payment pages, verify UPI proofs with OCR, block duplicate transactions, and organize every student by school, program, level and batch.</p>
        </div>
        <div className="relative z-10 text-slate-500 text-sm">Secure admin access • GDIOT PAY</div>
        <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-blue-600/20 blur-3xl" />
        <div className="absolute -left-10 bottom-10 w-72 h-72 rounded-full bg-blue-500/10 blur-3xl" />
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-slate-50">
        <div className="w-full max-w-md animate-fade-up">
          <div className="lg:hidden mb-8 flex justify-center"><Logo size="lg" align="center" /></div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-900/5 p-8">
            <h2 className="font-head text-2xl font-bold text-slate-900">Welcome back</h2>
            <p className="text-slate-500 text-sm mt-1 mb-6">Sign in to the GDIOT PAY admin panel.</p>

            {error && (
              <div data-testid="login-error" className="flex items-center gap-2 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg px-3.5 py-2.5 mb-4">
                <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
              </div>
            )}

            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Email or Mobile</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input data-testid="login-email" type="text" value={email} onChange={(e) => setEmail(e.target.value)} required
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg pl-10 pr-3.5 py-2.5 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="you@gdiot.in" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg pl-10 pr-3.5 py-2.5 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    placeholder="••••••••" />
                </div>
              </div>
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 text-slate-600 cursor-pointer">
                  <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="rounded border-slate-300 text-blue-600" /> Remember me
                </label>
                <Link to="/forgot-password" className="text-blue-600 font-medium hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <button data-testid="login-submit" type="submit" disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl shadow-md shadow-blue-500/20 active:scale-[0.99] transition-all flex items-center justify-center gap-2 disabled:opacity-60">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign In
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
